"use client";

import {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Sparkles } from "lucide-react";
import SolaIconBadge from "@components/shared/SolaIconBadge";
import {
  TextField,
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Button,
  Typography,
  Stack,
  Fade,
  Zoom,
  Slide,
  Alert,
  Snackbar,
} from "@mui/material";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@components/shared/DialogCompat";
import {
  Check as AcceptIcon,
  Close as RejectIcon,
  Undo as UndoIcon,
} from "@mui/icons-material";
import { ENHANCEMENT_OPTIONS } from "../enhancementPrompts";

export const AIInput = forwardRef(function AIInput({
  // Standard props
  value = "",
  onChange,
  placeholder = "",
  disabled = false,
  name,
  id,

  // InputAI specific props
  variant = "auto",
  autoExpandThreshold = 100,

  // AI Configuration
  prompt = "",

  // UI Customization
  enhanceButtonPosition = "inside",
  showEnhanceOnFocus = true,
  showEnhanceOnContent = true,
  showSuccessSnackbar = false,
  showErrorSnackbar = true,
  // Hides the built-in trigger UI entirely — the parent renders its own
  // toolbar/trigger and drives enhancement via ref.enhanceOption()/enhanceCustom().
  hideTrigger = false,
  onFocusChange,
  // Applies an enhancement immediately instead of opening the Original/
  // Enhanced preview dialog — matches AITextField's undo-based flow rather
  // than a confirm step.
  skipPreview = false,

  // Material-UI props
  label,
  helperText,
  error = false,
  required = false,
  fullWidth = true,
  size = "medium",

  // Textarea props
  minRows = 1,
  maxRows = 10,
  maxLength,

  // Callbacks
  onEnhancementStart,
  onEnhancementComplete,
  onEnhancementError,

  // Other props
  ...textFieldProps
}, ref) {
  // State management
  const [focused, setFocused] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [enhancedText, setEnhancedText] = useState("");
  const [originalText, setOriginalText] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [customPromptDialog, setCustomPromptDialog] = useState(false);
  const [userCustomPrompt, setUserCustomPrompt] = useState("");
  const [enhancementHistory, setEnhancementHistory] = useState([]);
  const [errorSnackbar, setErrorSnackbar] = useState("");
  const [successSnackbar, setSuccessSnackbar] = useState("");

  const textFieldRef = useRef(null);

  // Determine if we should use textarea
  const shouldUseTextarea =
    variant === "textarea" ||
    (variant === "auto" && value.length > autoExpandThreshold);

  // Show enhance button logic
  const showEnhanceButton =
    !disabled &&
    ((focused && showEnhanceOnFocus) || (value.trim() && showEnhanceOnContent));

  const handleEnhanceClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleEnhancementSelect = async (enhancementType) => {
    handleCloseMenu();

    if (enhancementType === "custom") {
      setCustomPromptDialog(true);
      return;
    }

    await performEnhancement(enhancementType);
  };

  const handleCustomEnhancement = async () => {
    setCustomPromptDialog(false);
    if (userCustomPrompt.trim()) {
      await performEnhancement("custom", userCustomPrompt);
    }
  };

  const performEnhancement = async (enhancementType, customPromptText = "") => {
    if (!value.trim()) return;

    setIsEnhancing(true);
    setOriginalText(value);
    onEnhancementStart?.();

    try {
      const response = await fetch("/api/enhance-text", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          text: value,
          enhancementType,
          prompt: prompt || "General text improvement",
          customPrompt: customPromptText,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `Enhancement failed with status ${response.status}`
        );
      }

      // Read the streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let enhancedText = "";

      while (true) {
        const { done, value: chunk } = await reader.read();
        if (done) break;
        enhancedText += decoder.decode(chunk, { stream: true });
      }

      // Clean up the response (remove any extra whitespace)
      const cleanedText = enhancedText.trim();

      // The model is instructed (see /api/enhance-text) to use this marker
      // instead of fabricating content when the input has nothing
      // meaningful to enhance (e.g. random characters) — surface it as a
      // real error rather than applying it to the field as if it were the
      // enhanced text.
      const REFUSAL_PREFIX = "UNENHANCEABLE:";
      if (cleanedText.startsWith(REFUSAL_PREFIX)) {
        // A fixed, app-controlled message rather than the model's own
        // (variably-phrased) reason — more predictable and consistent
        // than surfacing whatever wording the LLM happened to generate.
        const refusalError = new Error(
          "Too many random characters. Can't rewrite with AI — try editing the text first."
        );
        // Flags this as expected user-facing validation (surfaced via the
        // inline error banner, working as intended) rather than a genuine
        // failure — the catch block below skips console.error for it so it
        // doesn't trip Next.js's dev error overlay as if the app crashed.
        refusalError.expected = true;
        throw refusalError;
      }

      if (cleanedText) {
        if (skipPreview) {
          setEnhancementHistory((prev) => [
            ...prev,
            { original: value, enhanced: cleanedText },
          ]);
          onChange?.(cleanedText);
          onEnhancementComplete?.(value, cleanedText);
          setSuccessSnackbar("Text enhanced successfully!");
        } else {
          setEnhancedText(cleanedText);
          setShowPreview(true);
        }
      } else {
        throw new Error("No enhanced text received");
      }
    } catch (error) {
      if (!error.expected) {
        console.error("Enhancement error:", error);
      }
      onEnhancementError?.(error);

      // Show user-friendly error snackbar
      setErrorSnackbar(
        error.message || "Enhancement failed. Please try again."
      );
    } finally {
      setIsEnhancing(false);
    }
  };

  useImperativeHandle(ref, () => ({
    enhanceOption: (enhancementType) => performEnhancement(enhancementType),
    enhanceCustom: (customPromptText) =>
      performEnhancement("custom", customPromptText),
  }));

  const handleAcceptEnhancement = () => {
    // Add to history for undo functionality
    setEnhancementHistory((prev) => [
      ...prev,
      { original: value, enhanced: enhancedText },
    ]);

    onChange?.(enhancedText);
    onEnhancementComplete?.(originalText, enhancedText);
    setShowPreview(false);
    setEnhancedText("");
    setOriginalText("");
    setSuccessSnackbar("Text enhanced successfully!");
  };

  const handleRejectEnhancement = () => {
    setShowPreview(false);
    setEnhancedText("");
    setOriginalText("");
  };

  const handleUndo = () => {
    if (enhancementHistory.length > 0) {
      const lastEntry = enhancementHistory[enhancementHistory.length - 1];
      onChange?.(lastEntry.original);
      setEnhancementHistory((prev) => prev.slice(0, -1));
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!focused) return;

      if ((event.metaKey || event.ctrlKey) && event.key === "e") {
        event.preventDefault();
        if (showEnhanceButton) {
          handleEnhanceClick({ currentTarget: textFieldRef.current });
        }
      }

      if (showPreview) {
        if (event.key === "Enter" && !event.shiftKey) {
          event.preventDefault();
          handleAcceptEnhancement();
        }
        if (event.key === "Escape") {
          event.preventDefault();
          handleRejectEnhancement();
        }
      }

      if (
        (event.metaKey || event.ctrlKey) &&
        event.key === "z" &&
        enhancementHistory.length > 0
      ) {
        event.preventDefault();
        handleUndo();
      }
    };

    if (focused) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [focused, showEnhanceButton, showPreview, enhancementHistory.length]);

  return (
    <Box sx={{ position: "relative", width: fullWidth ? "100%" : "auto" }}>
      {/* Main Input Field */}
      <TextField
        ref={textFieldRef}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        onFocus={() => {
          setFocused(true);
          onFocusChange?.(true);
        }}
        onBlur={() => {
          setFocused(false);
          onFocusChange?.(false);
        }}
        placeholder={placeholder}
        disabled={disabled || isEnhancing}
        name={name}
        id={id}
        label={label}
        helperText={helperText}
        error={error}
        required={required}
        fullWidth={fullWidth}
        size={size}
        multiline={shouldUseTextarea}
        minRows={shouldUseTextarea ? minRows : undefined}
        maxRows={shouldUseTextarea ? maxRows : undefined}
        inputProps={{
          maxLength: maxLength,
        }}
        InputProps={{
          endAdornment: enhancementHistory.length > 0 && (
            <Fade in={showEnhanceButton} timeout={300}>
              <IconButton
                size="small"
                onClick={handleUndo}
                title="Undo last enhancement (Ctrl+Z)"
                sx={{
                  opacity: 0.7,
                  transition: "all 0.2s",
                  "&:hover": {
                    opacity: 1,
                    transform: "scale(1.1)",
                  },
                }}
              >
                <UndoIcon fontSize="small" />
              </IconButton>
            </Fade>
          ),
        }}
        {...textFieldProps}
      />

      {/* Improve with SOLA AI — sits underneath the field it enhances.
          Omitted when hideTrigger is set: the parent renders its own
          toolbar/trigger and drives enhancement via the ref API. */}
      {!hideTrigger && (
        <Fade in={showEnhanceButton} timeout={300}>
          <Box
            component="button"
            type="button"
            onClick={handleEnhanceClick}
            disabled={isEnhancing || !value.trim()}
            title="Improve with SOLA AI"
            sx={{
              display: "inline-flex",
              mt: 1,
              p: 0,
              border: "none",
              background: "none",
              cursor: "pointer",
              "&:disabled": { cursor: "default", opacity: 0.6 },
            }}
          >
            {isEnhancing ? (
              <CircularProgress size={16} sx={{ color: "inherit" }} />
            ) : (
              <SolaIconBadge size={16} icon={<Sparkles size={16} />} />
            )}
          </Box>
        </Fade>
      )}

      {/* Character count */}
      {maxLength && (
        <Box sx={{ textAlign: "right", mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {value.length} / {maxLength}
          </Typography>
        </Box>
      )}

      {/* Enhancement Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 300 }}
        PaperProps={{
          sx: {
            maxWidth: 320,
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            border: "1px solid",
            borderColor: "divider",
          },
        }}
        MenuListProps={{
          sx: { py: 1 },
        }}
      >
        {ENHANCEMENT_OPTIONS.map((option, index) => {
          const IconComponent = option.icon;
          return (
            <Slide
              key={option.id}
              direction="up"
              in={Boolean(anchorEl)}
              timeout={200 + index * 50}
              unmountOnExit
            >
              <MenuItem
                onClick={() => handleEnhancementSelect(option.id)}
                sx={{
                  py: 1.5,
                  px: 2,
                  mx: 0.5,
                  borderRadius: 1,
                  transition: "all 0.2s",
                  "&:hover": {
                    bgcolor: "primary.50",
                    transform: "translateX(4px)",
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <IconComponent
                    fontSize="small"
                    sx={{
                      color: "primary.main",
                      transition: "transform 0.2s",
                      ".Mui-selected &": {
                        transform: "scale(1.2)",
                      },
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={option.label}
                  secondary={option.description}
                  primaryTypographyProps={{
                    fontWeight: 500,
                    fontSize: "0.9rem",
                  }}
                  secondaryTypographyProps={{
                    variant: "caption",
                    sx: {
                      color: "text.secondary",
                      lineHeight: 1.3,
                    },
                  }}
                />
              </MenuItem>
            </Slide>
          );
        })}
      </Menu>

      {/* Custom Prompt Dialog */}
      <Dialog
        open={customPromptDialog}
        onClose={() => setCustomPromptDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Custom Enhancement</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label="Enhancement Instructions"
            placeholder="Describe how you want the text to be enhanced..."
            value={userCustomPrompt}
            onChange={(e) => setUserCustomPrompt(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomPromptDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCustomEnhancement}
            variant="contained"
            disabled={!userCustomPrompt.trim()}
          >
            Enhance
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhancement Preview */}
      <Dialog
        open={showPreview}
        onClose={handleRejectEnhancement}
        maxWidth="md"
        fullWidth
        TransitionComponent={Zoom}
        TransitionProps={{ timeout: 400 }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 16px 64px rgba(0,0,0,0.16)",
          },
        }}
      >
        <DialogTitle sx={{ fontSize: "16px" }}>Enhancement Preview</DialogTitle>
        <DialogContent>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={3}
            sx={{ width: "100%" }}
          >
            <Stack spacing={1} sx={{ flex: 1 }}>
              <Typography
                variant="subtitle2"
                gutterBottom
                color="text.secondary"
              >
                Original ({originalText.length} chars)
              </Typography>
              <Box
                sx={{
                  p: 2,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  bgcolor: "grey.50",
                  minHeight: 100,
                  fontSize: "0.875rem",
                  whiteSpace: "pre-wrap",
                  flex: 1,
                }}
              >
                {originalText}
              </Box>
            </Stack>
            <Stack spacing={1} sx={{ flex: 1 }}>
              <Typography variant="subtitle2" gutterBottom color="primary.main">
                Enhanced ({enhancedText.length} chars)
              </Typography>
              <Box
                sx={{
                  p: 2,
                  border: 1,
                  borderColor: "primary.main",
                  borderRadius: 1,
                  bgcolor: "primary.50",
                  minHeight: 100,
                  fontSize: "0.875rem",
                  whiteSpace: "pre-wrap",
                  flex: 1,
                }}
              >
                {enhancedText}
              </Box>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRejectEnhancement} startIcon={<RejectIcon />}>
            Reject
          </Button>
          <Button
            onClick={() => performEnhancement("improve")}
            disabled={isEnhancing}
          >
            Try Again
          </Button>
          <Button
            onClick={handleAcceptEnhancement}
            variant="contained"
            startIcon={<AcceptIcon />}
          >
            Accept (Enter)
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={!!successSnackbar && showSuccessSnackbar}
        autoHideDuration={3000}
        onClose={() => setSuccessSnackbar("")}
        TransitionComponent={Slide}
        TransitionProps={{ direction: "up" }}
      >
        <Alert
          onClose={() => setSuccessSnackbar("")}
          severity="success"
          variant="filled"
          sx={{
            borderRadius: 2,
            "& .MuiAlert-icon": {
              fontSize: "1.25rem",
            },
          }}
        >
          {successSnackbar}
        </Alert>
      </Snackbar>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={!!errorSnackbar && showErrorSnackbar}
        autoHideDuration={5000}
        onClose={() => setErrorSnackbar("")}
        TransitionComponent={Slide}
        TransitionProps={{ direction: "up" }}
      >
        <Alert
          onClose={() => setErrorSnackbar("")}
          severity="error"
          variant="filled"
          sx={{
            borderRadius: 2,
            "& .MuiAlert-icon": {
              fontSize: "1.25rem",
            },
          }}
        >
          {errorSnackbar}
        </Alert>
      </Snackbar>
    </Box>
  );
});

AIInput.displayName = "AIInput";
